import { useState } from "react";
import {
  inputClass,
  mfgCompactGhostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  columnAppliesRecipeIds,
  sameRecipeIdList,
} from "@/lib/manufacturing/cogsColumnProductScope";
import { isOverheadCogsColumn } from "@/lib/manufacturing/overheadCogsLink";
import {
  COLUMN_BAND_LABEL,
  COLUMN_BANDS,
  COLUMN_CALC_OPTIONS,
  columnAppliesToProductsOnly,
  columnCalcFromMetadata,
  columnCalcKind,
  FORMULA_BASIS_OPTIONS,
  isFormulaKind,
  type ColumnCalcKind,
} from "@/lib/manufacturing/cogsColumnEdit";
import type { HaulRateBasis } from "@/lib/manufacturing/haulRateEvaluate";
import type { MfgPricingLayerBand, MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import { PricingColumnProductScope } from "./PricingColumnProductScope";

export type ColumnDraft = {
  label: string;
  band: MfgPricingLayerBand;
  calcKind: ColumnCalcKind;
  basis: HaulRateBasis;
  rateUsd: number | null;
  productsOnly: boolean;
  /** Null prices every row the formula covers. */
  recipeIds: string[] | null;
};

function calcOptions(current: ColumnCalcKind) {
  if (current === "labor" || current === "overhead") {
    return [
      ...COLUMN_CALC_OPTIONS,
      { id: current, label: current === "labor" ? "Labor (custom)" : "Overhead (custom)" },
    ];
  }
  return COLUMN_CALC_OPTIONS;
}

export function parsedRate(text: string): number | null {
  if (!text.trim()) return null;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function storedCalc(column: MfgPricingLayerColumn): { basis: HaulRateBasis; rate: string } {
  const calc = columnCalcFromMetadata(column.metadata);
  if (!calc || calc.basis === "none") return { basis: calc?.basis ?? "building_sq_ft", rate: "" };
  return { basis: calc.basis, rate: String(calc.rate_usd) };
}

export function FormulaFields({
  basis,
  rate,
  locked,
  onBasis,
  onRate,
}: {
  basis: HaulRateBasis;
  rate: string;
  locked: boolean;
  onBasis: (basis: HaulRateBasis) => void;
  onRate: (rate: string) => void;
}) {
  const option = FORMULA_BASIS_OPTIONS.find((item) => item.id === basis);
  return (
    <>
      <select
        aria-label="Calculation basis"
        className={`${inputClass} w-full sm:w-36`}
        value={basis}
        onChange={(e) => onBasis(e.target.value as HaulRateBasis)}
        disabled={locked}
      >
        {FORMULA_BASIS_OPTIONS.map((item) => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>
      {basis === "none" ? null : (
        <label className="block w-full text-sm sm:w-24">
          <span className="sr-only">{option?.rateLabel || "Rate"}</span>
          <input
            className={inputClass}
            inputMode="decimal"
            value={rate}
            placeholder={option?.rateLabel || "Rate"}
            onChange={(e) => onRate(e.target.value)}
            disabled={locked}
          />
        </label>
      )}
    </>
  );
}

export function PricingColumnEditor({
  column,
  rows,
  locked,
  canUp,
  canDown,
  onMove,
  onSave,
  onDetach,
}: {
  column: MfgPricingLayerColumn;
  rows: MfgPricingLayerGridRow[];
  locked: boolean;
  canUp: boolean;
  canDown: boolean;
  onMove: (direction: -1 | 1) => void;
  onSave: (draft: ColumnDraft) => void;
  onDetach?: () => void;
}) {
  const stored = storedCalc(column);
  const storedIds = columnAppliesRecipeIds(column.metadata);
  const [label, setLabel] = useState(column.label);
  const [band, setBand] = useState(column.band);
  const [calcKind, setCalcKind] = useState<ColumnCalcKind>(columnCalcKind(column));
  const [basis, setBasis] = useState<HaulRateBasis>(stored.basis);
  const [rate, setRate] = useState(stored.rate);
  const [productsOnly, setProductsOnly] = useState(columnAppliesToProductsOnly(column));
  const [limitProducts, setLimitProducts] = useState(storedIds != null);
  const [recipeIds, setRecipeIds] = useState<string[]>(storedIds ?? []);
  const formula = isFormulaKind(calcKind);
  const overheadGuest = isOverheadCogsColumn(column);
  const rateUsd = basis === "none" ? 0 : parsedRate(rate);
  const storedRate = stored.basis === "none" ? 0 : parsedRate(stored.rate);
  const pickedIds = formula && limitProducts ? recipeIds : null;
  const rateInvalid = formula && basis !== "none" && rate.trim() !== "" && rateUsd === null;
  const scopeInvalid = pickedIds != null && pickedIds.length === 0;
  const dirty =
    label.trim() !== column.label ||
    band !== column.band ||
    calcKind !== columnCalcKind(column) ||
    (formula &&
      (basis !== stored.basis ||
        rateUsd !== storedRate ||
        productsOnly !== columnAppliesToProductsOnly(column) ||
        !sameRecipeIdList(pickedIds, storedIds)));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="block w-full text-sm sm:w-1/8 sm:min-w-[4rem]">
        <span className="sr-only">Label</span>
        <input className={inputClass} value={label} onChange={(e) => setLabel(e.target.value)} disabled={locked} />
      </label>
      <select
        aria-label="Calculation"
        className={`${inputClass} w-full sm:w-1/6 sm:min-w-[4.5rem]`}
        value={calcKind}
        onChange={(e) => setCalcKind(e.target.value as ColumnCalcKind)}
        disabled={locked || overheadGuest}
        hidden={overheadGuest}
      >
        {calcOptions(columnCalcKind(column)).map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      {formula ? (
        <>
          <FormulaFields basis={basis} rate={rate} locked={locked} onBasis={setBasis} onRate={setRate} />
          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={productsOnly}
              onChange={(e) => setProductsOnly(e.target.checked)}
              disabled={locked}
            />
            Products only
          </label>
          <div className="w-full">
            <PricingColumnProductScope
              rows={rows}
              productsOnly={productsOnly}
              enabled={limitProducts}
              selectedIds={recipeIds}
              locked={locked}
              onEnabled={setLimitProducts}
              onSelectedIds={setRecipeIds}
            />
          </div>
        </>
      ) : null}
      <select
        aria-label="Band"
        className={`${inputClass} w-full sm:w-28`}
        value={band}
        onChange={(e) => setBand(e.target.value as MfgPricingLayerBand)}
        disabled={locked || overheadGuest}
        hidden={overheadGuest}
      >
        {COLUMN_BANDS.map((id) => (
          <option key={id} value={id}>
            {COLUMN_BAND_LABEL[id]}
          </option>
        ))}
      </select>
      <div className="flex gap-1">
        <button type="button" className={mfgCompactGhostBtnClass} disabled={locked || !canUp} onClick={() => onMove(-1)}>
          Up
        </button>
        <button type="button" className={mfgCompactGhostBtnClass} disabled={locked || !canDown} onClick={() => onMove(1)}>
          Down
        </button>
      </div>
      <button
        type="button"
        className={mfgPrimaryBtnClass}
        disabled={locked || !dirty || !label.trim() || rateInvalid || scopeInvalid || (limitProducts && formula && rateUsd === null)}
        onClick={() => onSave({ label: label.trim(), band, calcKind, basis, rateUsd, productsOnly, recipeIds: pickedIds })}
      >
        Save
      </button>
      {onDetach && !overheadGuest ? (
        <button type="button" className={mfgCompactGhostBtnClass} disabled={locked} onClick={onDetach}>
          Take off the grid
        </button>
      ) : null}
      {overheadGuest ? (
        <p className="w-full text-[10px] text-gray-500 dark:text-zinc-400">
          Calculated on the Overhead screen. Rename it here, or move it within COGS.
        </p>
      ) : null}
      {calcKind === "margin_denom" || calcKind === "markup" ? (
        <p className="w-full text-[10px] text-gray-500 dark:text-zinc-400">
          {calcKind === "margin_denom"
            ? "Margin: 0.15 means 15% of the final price."
            : "Markup: 0.15 means 15% added to the number underneath."}
        </p>
      ) : null}
    </div>
  );
}
